import { useEffect, useState } from "react";

/**
 * Advisory only: client clocks and offline snapshots cannot prove server failure.
 * The UI may show a waiting-for-status hint; the server owns terminal outcomes.
 */
const QUEUED_STALE_MS = 10 * 60 * 1000;
const PROCESSING_STALE_MS = 20 * 60 * 1000;

type StaleCheckInput = {
  processing?: { status?: string | null } | null;
  updatedAt?: string | null;
  createdAt?: string | null;
  isDeleted?: boolean | null;
};

/** How often staleness is re-evaluated while something is still in flight. */
const STALE_TICK_MS = 30 * 1000;

const parseTimestamp = (value?: string | null): number | null => {
  if (!value) return null;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? null : parsed;
};

export const isStuckTranscription = (transcription: StaleCheckInput): boolean => {
  if (transcription.isDeleted) {
    return false;
  }

  const status = transcription.processing?.status;
  if (status !== "queued" && status !== "processing") {
    return false;
  }

  const lastChangedAt = parseTimestamp(transcription.updatedAt) ?? parseTimestamp(transcription.createdAt);
  if (lastChangedAt === null) {
    return false;
  }

  const threshold = status === "queued" ? QUEUED_STALE_MS : PROCESSING_STALE_MS;
  return Date.now() - lastChangedAt > threshold;
};

export const hasActiveTranscription = (transcriptions: StaleCheckInput[]): boolean =>
  transcriptions.some(
    (item) =>
      !item.isDeleted &&
      (item.processing?.status === "queued" || item.processing?.status === "processing")
  );

/**
 * A stuck record produces no document update, so nothing re-renders on its own.
 * This drives a periodic re-evaluation while work is still in flight.
 */
export const useStaleTick = (enabled: boolean): number => {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!enabled) return;

    const interval = setInterval(() => setTick((value) => value + 1), STALE_TICK_MS);
    return () => clearInterval(interval);
  }, [enabled]);

  return tick;
};
